<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(defineProps<{ functionName?: string; sizeClass?: string }>(), {
  functionName: undefined,
  sizeClass: 'w-10 h-10',
})

const CATEGORY: Record<string, string> = {
  GetCompanyCreditSummary: 'finance',
  GetCompanyOrdersData: 'finance',
  SearchMarketingRatios: 'search',
  ConsultAllPlanners: 'thinking',
  RunCompaniesAgent: 'search',
  RunTransportRoadAgent: 'truck',
  RunSeaAirAgent: 'ship',
  RunLogisticsAgent: 'warehouse',
  RunFinanceAgent: 'finance',
  RunUsersAgent: 'users',
  RunContractsAgent: 'contract',
  RunAccountReceivablesAgent: 'overdue',
  RunStatusesAgent: 'flag',
  RunOptionsAgent: 'gears',
  RunAnnouncementsAgent: 'bell',
  RunAcceptedEmailDomainsAgent: 'mail',
  RunActivitiesAgent: 'clock',
  RunShipmentActivitiesAgent: 'route',
  RunCurrenciesAgent: 'exchange',
  RunDepartmentsAgent: 'building',
  RunLegalHolidaysAgent: 'calendar',
  RunShippersAgent: 'truck',
  RunOrderEntryAgent: 'clipboard',
  SearchCompanies: 'search',
  GetCompanyWithBusinessLines: 'search',
  SearchCountries: 'search',
  SearchContacts: 'users',
  SearchUsers: 'users',
  SearchClientUsers: 'users',
  GetKeyAccountManagers: 'users',
  GetSalesPersons: 'users',
  SearchIndoorSales: 'users',
  SearchRoles: 'gears',
  SearchClientRoles: 'gears',
  SearchClientPermissions: 'gears',
  GetUser: 'users',
  GetUserBusinessLineAccess: 'users',
  GetUserCompanyAccess: 'users',
  SearchDomesticShipments: 'truck',
  SearchInternationalShipments: 'truck',
  SearchStrongoShipments: 'truck',
  SearchSeaShipments: 'ship',
  SearchAirShipments: 'plane',
  SearchInbounds: 'warehouse',
  SearchOutbounds: 'warehouse',
  SearchVbsInbounds: 'warehouse',
  SearchVbsOutbounds: 'warehouse',
  SearchCustomerLedgerEntries: 'finance',
  GetCustomerLedgerEntryWithGlEntries: 'finance',
  SearchCustomerLedgerGlEntries: 'finance',
  GetErpInvoiceLineNames: 'finance',
  GetGlTransportTypes: 'truck',
  GetExchangeRates: 'exchange',
  GetUnpaidInvoiceSummary: 'finance',
  RangeQuery: 'gears',
  SearchAccountReceivables: 'overdue',
  SearchReleases: 'bell',
  SearchMaintenances: 'gears',
  SearchSiteNotifications: 'bell',
  GetCurrentDateTime: 'clock',
  SearchApiSources: 'flag',
  SearchApiStatuses: 'flag',
  SearchApiSubstatuses: 'flag',
  SearchAppStatuses: 'flag',
  GetDataSyncStatus: 'gears',
  RunDocumentationAgent: 'book',
  SearchDocumentation: 'book',
  SearchOptionCategories: 'gears',
  SearchOptionValues: 'gears',
  SearchAcceptedEmailDomains: 'mail',
  SearchKlgAllowedEmailDomains: 'mail',
  SearchWebmailDomains: 'mail',
  SearchActivityTypes: 'clock',
  SearchActivityStatuses: 'clock',
  SearchDomesticRoadActivities: 'route',
  SearchInternationalRoadActivities: 'route',
  SearchStrongoActivities: 'route',
  SearchTooltipCategories: 'gears',
  SearchTooltipValues: 'gears',
  SearchCurrencies: 'exchange',
  SearchDepartments: 'building',
  SearchLegalHolidays: 'calendar',
  SearchShippers: 'truck',
  GetCompanyContracts: 'contract',
  GetContractManagers: 'contract',
  GetCompanyDomesticOrderEntry: 'clipboard',
  GetCompanyInternationalOrderEntry: 'clipboard',
  GetDomesticOrderEntryGeneralSettings: 'clipboard',
  GetInternationalOrderEntryGeneralSettings: 'clipboard',
  GetStrongoOrderEntryGeneralSettings: 'clipboard',
  SearchIncoterms: 'gears',
  SearchPackages: 'warehouse',
  SearchOrderEntryFeatures: 'clipboard',
}

const category = computed(() => {
  if (!props.functionName) return 'thinking'
  return CATEGORY[props.functionName] ?? 'thinking'
})
</script>

<template>

  <svg v-if="category === 'thinking'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <g class="cti-eye-scan">
      <circle cx="16" cy="26" r="3.6" fill="#004AAD"/>
      <circle cx="17" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    </g>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <g class="cti-eye-scan">
      <circle cx="32" cy="26" r="3.6" fill="#004AAD"/>
      <circle cx="33" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    </g>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <circle cx="40" cy="10" r="1.4" fill="#FCD34D" class="cti-td-a"/>
    <circle cx="43.5" cy="6" r="1.9" fill="#FCD34D" class="cti-td-b"/>
    <circle cx="47.5" cy="2" r="2.4" fill="#FCD34D" class="cti-td-c"/>
  </svg>

  <svg v-else-if="category === 'search'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="17" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="8" fill="#e8f0fe" stroke="#004AAD" stroke-width="2.2"/>
    <circle cx="32" cy="26" r="4.2" fill="#004AAD"/>
    <circle cx="33.2" cy="24.5" r="1.5" fill="white" fill-opacity="0.85"/>
    <g class="cti-radar" style="transform-origin:32px 26px;">
      <line x1="32" y1="26" x2="32" y2="18" stroke="#F59E0B" stroke-width="2" stroke-linecap="round"/>
      <path d="M32 26 L32 18 A8 8 0 0 1 40 26 Z" fill="#F59E0B" fill-opacity="0.18"/>
    </g>
    <line x1="38" y1="32" x2="51" y2="46" stroke="#D97706" stroke-width="3.5" stroke-linecap="round"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
  </svg>

  <svg v-else-if="category === 'truck'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16.3" cy="26.8" r="3.6" fill="#004AAD"/>
    <circle cx="17.3" cy="25.5" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32.3" cy="26.8" r="3.6" fill="#004AAD"/>
    <circle cx="33.3" cy="25.5" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <rect x="-8" y="17" width="10" height="6" rx="1.5" fill="#0056cc"/>
    <rect x="-7" y="18" width="7" height="4" rx="1" fill="#93c5fd" fill-opacity="0.65"/>
    <rect x="46" y="17" width="10" height="6" rx="1.5" fill="#0056cc"/>
    <rect x="47" y="18" width="7" height="4" rx="1" fill="#93c5fd" fill-opacity="0.65"/>
    <rect x="2" y="47" width="44" height="9" rx="2" fill="#1e293b"/>
    <circle cx="10" cy="51" r="2.5" fill="#FCD34D" opacity="0.7"/>
    <circle cx="38" cy="51" r="2.5" fill="#FCD34D" opacity="0.7"/>
    <rect x="17" y="49" width="14" height="4" rx="1.5" fill="#0f172a"/>
    <g>
      <animateTransform attributeName="transform" type="rotate"
        values="-14 24 45; 14 24 45; -14 24 45"
        keyTimes="0; 0.5; 1"
        keySplines="0.4 0 0.6 1; 0.4 0 0.6 1"
        calcMode="spline" dur="1.8s" repeatCount="indefinite"/>
      <circle cx="24" cy="45" r="10" stroke="#1e293b" stroke-width="3" fill="none"/>
      <line x1="24" y1="45" x2="24" y2="35" stroke="#1e293b" stroke-width="2.5" stroke-linecap="round"/>
      <line x1="24" y1="45" x2="32.7" y2="50" stroke="#1e293b" stroke-width="2.5" stroke-linecap="round"/>
      <line x1="24" y1="45" x2="15.3" y2="50" stroke="#1e293b" stroke-width="2.5" stroke-linecap="round"/>
      <circle cx="24" cy="45" r="3.5" fill="#1e293b"/>
      <circle cx="24" cy="45" r="2" fill="#004AAD"/>
    </g>
  </svg>

  <svg v-else-if="category === 'ship'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <g class="cti-ship-bob">
      <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
      <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
      <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
      <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
      <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
      <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
      <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
      <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
      <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
      <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
      <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
      <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
      <circle cx="16" cy="26" r="6.5" fill="white"/>
      <circle cx="16.3" cy="26.8" r="3.6" fill="#004AAD"/>
      <circle cx="17.3" cy="25.5" r="1.3" fill="white" fill-opacity="0.85"/>
      <circle cx="32" cy="26" r="6.5" fill="white"/>
      <circle cx="32.3" cy="26.8" r="3.6" fill="#004AAD"/>
      <circle cx="33.3" cy="25.5" r="1.3" fill="white" fill-opacity="0.85"/>
      <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
      <rect x="2" y="47" width="44" height="9" rx="2" fill="#0c4a6e"/>
      <rect x="2" y="47" width="44" height="3" rx="1" fill="#0369a1" opacity="0.7"/>
      <g>
        <animateTransform attributeName="transform" type="rotate"
          values="-12 24 45; 12 24 45; -12 24 45"
          keyTimes="0; 0.5; 1"
          keySplines="0.4 0 0.6 1; 0.4 0 0.6 1"
          calcMode="spline" dur="2.2s" repeatCount="indefinite"/>
        <circle cx="24" cy="45" r="10" stroke="#92400E" stroke-width="2.5" fill="#78350f" fill-opacity="0.2"/>
        <line x1="24" y1="45" x2="24" y2="35" stroke="#92400E" stroke-width="2" stroke-linecap="round"/>
        <line x1="24" y1="45" x2="32.7" y2="40" stroke="#92400E" stroke-width="2" stroke-linecap="round"/>
        <line x1="24" y1="45" x2="32.7" y2="50" stroke="#92400E" stroke-width="2" stroke-linecap="round"/>
        <line x1="24" y1="45" x2="24" y2="55" stroke="#92400E" stroke-width="2" stroke-linecap="round"/>
        <line x1="24" y1="45" x2="15.3" y2="50" stroke="#92400E" stroke-width="2" stroke-linecap="round"/>
        <line x1="24" y1="45" x2="15.3" y2="40" stroke="#92400E" stroke-width="2" stroke-linecap="round"/>
        <circle cx="24" cy="35" r="2.2" fill="#D97706"/>
        <circle cx="32.7" cy="40" r="2.2" fill="#D97706"/>
        <circle cx="32.7" cy="50" r="2.2" fill="#D97706"/>
        <circle cx="24" cy="55" r="2.2" fill="#D97706"/>
        <circle cx="15.3" cy="50" r="2.2" fill="#D97706"/>
        <circle cx="15.3" cy="40" r="2.2" fill="#D97706"/>
        <circle cx="24" cy="45" r="3.5" fill="#92400E"/>
        <circle cx="24" cy="45" r="2" fill="#D97706"/>
      </g>
    </g>
    <g class="cti-waves">
      <path d="M-4 56 Q2 52 8 56 Q14 60 20 56 Q26 52 32 56 Q38 60 44 56 Q50 52 56 56" stroke="#60a5fa" stroke-width="2" stroke-linecap="round" fill="none"/>
      <path d="M0 61 Q6 57 12 61 Q18 65 24 61 Q30 57 36 61 Q42 65 48 61" stroke="#93c5fd" stroke-width="1.5" stroke-linecap="round" fill="none" opacity="0.6"/>
    </g>
  </svg>

  <svg v-else-if="category === 'plane'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <path d="M 10 30 L -22 46 L -18 53 L 10 42 Z" fill="#004AAD"/>
    <path d="M 38 30 L 70 46 L 66 53 L 38 42 Z" fill="#004AAD"/>
    <line x1="7" y1="33" x2="-19" y2="48" stroke="#60a5fa" stroke-width="1" stroke-opacity="0.5"/>
    <line x1="41" y1="33" x2="67" y2="48" stroke="#60a5fa" stroke-width="1" stroke-opacity="0.5"/>
    <path d="M 20 44 L 16 58 L 32 58 L 28 44 Z" fill="#0056cc"/>
    <path d="M 13 46 L -1 54 L 1 58 L 13 52 Z" fill="#0056cc"/>
    <path d="M 35 46 L 49 54 L 47 58 L 35 52 Z" fill="#0056cc"/>
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16" cy="25.2" r="3.6" fill="#004AAD"/>
    <circle cx="17" cy="24" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32" cy="25.2" r="3.6" fill="#004AAD"/>
    <circle cx="33" cy="24" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M14 37 Q24 46 34 37" stroke="#92400E" stroke-width="2.5" stroke-linecap="round" fill="none" opacity="0.8"/>
    <g>
      <animateTransform attributeName="transform" type="translate" from="0 0" to="-48 0" dur="3s" repeatCount="indefinite"/>
      <path d="M 2 66 Q 6 62 11 65 Q 15 62 20 65 Q 20 69 2 69 Z" fill="white" fill-opacity="0.65"/>
      <path d="M 28 63 Q 34 59 39 62 Q 43 60 47 62 Q 47 66 28 66 Z" fill="white" fill-opacity="0.45"/>
      <path d="M 50 66 Q 54 62 59 65 Q 63 62 68 65 Q 68 69 50 69 Z" fill="white" fill-opacity="0.65"/>
      <path d="M 76 63 Q 82 59 87 62 Q 91 60 95 62 Q 95 66 76 66 Z" fill="white" fill-opacity="0.45"/>
    </g>
  </svg>

  <svg v-else-if="category === 'warehouse'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16.5" cy="27.2" r="3.6" fill="#004AAD"/>
    <circle cx="17.5" cy="26" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32.5" cy="27.2" r="3.6" fill="#004AAD"/>
    <circle cx="33.5" cy="26" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <rect x="2" y="47" width="44" height="10" rx="2" fill="#1e293b"/>
    <circle cx="10" cy="52" r="2.5" fill="#FCD34D" opacity="0.7"/>
    <circle cx="38" cy="52" r="2.5" fill="#FCD34D" opacity="0.7"/>
    <rect x="5" y="37" width="3.5" height="13" rx="1" fill="#374151"/>
    <rect x="10" y="37" width="3" height="13" rx="1" fill="#4b5563"/>
    <rect x="5" y="54" width="36" height="2.5" rx="1" fill="#D97706"/>
    <rect x="5" y="57" width="36" height="2.5" rx="1" fill="#D97706"/>
    <g>
      <animateTransform attributeName="transform" type="translate"
        values="0 0; 0 -13; 0 -13; 0 0"
        keyTimes="0; 0.4; 0.65; 1"
        keySplines="0.4 0 0.6 1; 0 0 1 1; 0.4 0 0.6 1"
        calcMode="spline" dur="2.2s" repeatCount="indefinite"/>
      <rect x="14" y="42" width="22" height="13" rx="2" fill="#F59E0B"/>
      <line x1="25" y1="42" x2="25" y2="55" stroke="#D97706" stroke-width="1" stroke-opacity="0.6"/>
      <line x1="14" y1="48" x2="36" y2="48" stroke="#D97706" stroke-width="1" stroke-opacity="0.6"/>
      <path d="M22 44.5 L25 41.5 L28 44.5" stroke="#D97706" stroke-width="1.3" fill="none" stroke-linecap="round"/>
    </g>
  </svg>

  <svg v-else-if="category === 'finance'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16.5" cy="27.5" r="4" fill="#004AAD"/>
    <circle cx="17.8" cy="26.2" r="1.5" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32.5" cy="27.5" r="4" fill="#004AAD"/>
    <circle cx="33.8" cy="26.2" r="1.5" fill="white" fill-opacity="0.85"/>
    <path d="M14 37 Q24 46 34 37" stroke="#92400E" stroke-width="2.5" stroke-linecap="round" fill="none" opacity="0.8"/>
    <rect x="5" y="52" width="38" height="5" rx="2" fill="#1e293b"/>
    <circle cx="11" cy="50" r="5.5" fill="#FCD34D" stroke="#D97706" stroke-width="1"/>
    <text x="11" y="52.3" text-anchor="middle" fill="#92400E" font-size="6" font-weight="bold">€</text>
    <circle cx="21" cy="50" r="5.5" fill="#F59E0B" stroke="#D97706" stroke-width="1"/>
    <text x="21" y="52.3" text-anchor="middle" fill="#92400E" font-size="6" font-weight="bold">$</text>
    <circle cx="31" cy="50" r="5.5" fill="#FCD34D" stroke="#D97706" stroke-width="1"/>
    <text x="31" y="52.3" text-anchor="middle" fill="#92400E" font-size="6" font-weight="bold">€</text>
    <circle cx="41" cy="50" r="5.5" fill="#F59E0B" stroke="#D97706" stroke-width="1"/>
    <g>
      <animateTransform attributeName="transform" type="translate"
        values="0 0; 0 11; 0 9; 0 11; 0 11"
        keyTimes="0; 0.38; 0.52; 0.64; 1"
        keySplines="0.4 0 0.8 1; 0 0 0.4 1; 0.4 0 0.8 1; 0 0 1 1"
        calcMode="spline" dur="1.8s" repeatCount="indefinite"/>
      <circle cx="24" cy="35" r="7" fill="#FCD34D" stroke="#D97706" stroke-width="2"/>
      <text x="24" y="38" text-anchor="middle" fill="#92400E" font-size="8" font-weight="bold">$</text>
    </g>
  </svg>

  <svg v-else-if="category === 'users'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="17" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="33" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <g class="cti-person-l">
      <circle cx="-3" cy="22" r="4" fill="#004AAD"/>
      <path d="M-10 36 C-10 28 4 28 4 36" fill="#004AAD"/>
    </g>
    <g class="cti-person-r">
      <circle cx="51" cy="22" r="4" fill="#004AAD"/>
      <path d="M44 36 C44 28 58 28 58 36" fill="#004AAD"/>
    </g>
    <circle cx="-3" cy="22" r="7" stroke="#F59E0B" stroke-width="1.5" fill="none" class="cti-pulse-l"/>
    <circle cx="51" cy="22" r="7" stroke="#F59E0B" stroke-width="1.5" fill="none" class="cti-pulse-r"/>
  </svg>

  <svg v-else-if="category === 'gears'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <g class="cti-gear-cw" style="transform-origin:6px 28px;">
      <circle cx="6" cy="28" r="4.5" fill="#D97706"/>
      <circle cx="6" cy="28" r="2" fill="#F59E0B"/>
      <rect x="4" y="22.5" width="4" height="3" rx="1" fill="#D97706"/>
      <rect x="4" y="30.5" width="4" height="3" rx="1" fill="#D97706"/>
      <rect x="0.5" y="26" width="3" height="4" rx="1" fill="#D97706"/>
      <rect x="8.5" y="26" width="3" height="4" rx="1" fill="#D97706"/>
    </g>
    <g class="cti-gear-ccw" style="transform-origin:42px 28px;">
      <circle cx="42" cy="28" r="4.5" fill="#D97706"/>
      <circle cx="42" cy="28" r="2" fill="#F59E0B"/>
      <rect x="40" y="22.5" width="4" height="3" rx="1" fill="#D97706"/>
      <rect x="40" y="30.5" width="4" height="3" rx="1" fill="#D97706"/>
      <rect x="36.5" y="26" width="3" height="4" rx="1" fill="#D97706"/>
      <rect x="44.5" y="26" width="3" height="4" rx="1" fill="#D97706"/>
    </g>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="17" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="33" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
  </svg>

  <svg v-else-if="category === 'bell'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <g class="cti-bell-ring" style="transform-origin:24px 2px;">
      <path d="M24 -5 C17 -5 13 1 13 8 L35 8 C35 1 31 -5 24 -5 Z" fill="#F59E0B"/>
      <path d="M24 -5 C20 -5 17 0 17 7" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-opacity="0.4" fill="none"/>
      <circle cx="24" cy="10" r="2.5" fill="#D97706"/>
    </g>
    <path d="M11 4 Q7 0 11 -4" stroke="#FCD34D" stroke-width="1.5" stroke-linecap="round" fill="none" class="cti-ring-l"/>
    <path d="M37 4 Q41 0 37 -4" stroke="#FCD34D" stroke-width="1.5" stroke-linecap="round" fill="none" class="cti-ring-r"/>
    <rect x="22.5" y="8" width="3" height="5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="17" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="33" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
  </svg>

  <svg v-else-if="category === 'clock'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="17.2" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="18.4" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="33" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <circle cx="-1" cy="28" r="9" fill="white" stroke="#004AAD" stroke-width="2"/>
    <circle cx="-1" cy="28" r="7.5" fill="#f0f4ff"/>
    <line x1="-1" y1="22" x2="-1" y2="24.5" stroke="#004AAD" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="-1" y1="31.5" x2="-1" y2="34" stroke="#004AAD" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="-8" y1="28" x2="-5.5" y2="28" stroke="#004AAD" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="3.5" y1="28" x2="6" y2="28" stroke="#004AAD" stroke-width="1.5" stroke-linecap="round"/>
    <line x1="-1" y1="28" x2="-5" y2="23" stroke="#1e293b" stroke-width="2" stroke-linecap="round"/>
    <g class="cti-clock-min" style="transform-origin:-1px 28px;">
      <line x1="-1" y1="28" x2="-1" y2="21" stroke="#004AAD" stroke-width="1.5" stroke-linecap="round"/>
    </g>
    <g class="cti-clock-sec" style="transform-origin:-1px 28px;">
      <line x1="-1" y1="30" x2="-1" y2="21" stroke="#F59E0B" stroke-width="1" stroke-linecap="round"/>
    </g>
    <circle cx="-1" cy="28" r="1.5" fill="#004AAD"/>
  </svg>

  <svg v-else-if="category === 'contract'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16.5" cy="27.2" r="3.6" fill="#004AAD"/>
    <circle cx="17.5" cy="26" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32.5" cy="27.2" r="3.6" fill="#004AAD"/>
    <circle cx="33.5" cy="26" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <rect x="3" y="47" width="42" height="12" rx="2" fill="white" stroke="#e2e8f0" stroke-width="1.2"/>
    <line x1="6" y1="51" x2="42" y2="51" stroke="#e2e8f0" stroke-width="0.8"/>
    <line x1="6" y1="55" x2="42" y2="55" stroke="#e2e8f0" stroke-width="0.8"/>
    <path d="M8 53 Q13 48 18 53 Q22 57 27 52 Q31 47 36 53"
      stroke="#1e293b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
      fill="none" class="cti-signature" stroke-dasharray="58" stroke-dashoffset="58"/>
    <g>
      <animateTransform attributeName="transform" type="translate"
        values="0 0; 28 0; 28 0; 0 0"
        keyTimes="0; 0.6; 0.8; 1"
        keySplines="0.4 0 0.6 1; 0 0 1 1; 0.4 0 0.6 1"
        calcMode="spline" dur="2.5s" repeatCount="indefinite"/>
      <line x1="6" y1="57" x2="13" y2="49" stroke="#004AAD" stroke-width="3" stroke-linecap="round"/>
      <circle cx="6" cy="57" r="1.5" fill="#1e293b"/>
      <circle cx="13" cy="49" r="2" fill="#F59E0B"/>
    </g>
  </svg>

  <svg v-else-if="category === 'book'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16.5" cy="27.4" r="3.6" fill="#004AAD"/>
    <circle cx="17.5" cy="26.2" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32.5" cy="27.4" r="3.6" fill="#004AAD"/>
    <circle cx="33.5" cy="26.2" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <path d="M24 49 C18 47 10 46.5 3.5 48 L3.5 59.5 C10 58 18 58.5 24 60.5 Z" fill="#004AAD"/>
    <path d="M24 49 C30 47 38 46.5 44.5 48 L44.5 59.5 C38 58 30 58.5 24 60.5 Z" fill="#0056cc"/>
    <path d="M24 47.5 C18 45.5 11.5 45 5.5 46.5 L5.5 57.2 C11.5 55.7 18 56.2 24 58.2 Z" fill="white" stroke="#cbd5e1" stroke-width="1"/>
    <path d="M24 47.5 C30 45.5 36.5 45 42.5 46.5 L42.5 57.2 C36.5 55.7 30 56.2 24 58.2 Z" fill="white" stroke="#cbd5e1" stroke-width="1"/>
    <line x1="9" y1="50" x2="20" y2="51.1" stroke="#cbd5e1" stroke-width="1" stroke-linecap="round"/>
    <line x1="9" y1="52.6" x2="20" y2="53.7" stroke="#cbd5e1" stroke-width="1" stroke-linecap="round"/>
    <line x1="9" y1="55.2" x2="17" y2="56" stroke="#cbd5e1" stroke-width="1" stroke-linecap="round"/>
    <line x1="39" y1="50" x2="28" y2="51.1" stroke="#cbd5e1" stroke-width="1" stroke-linecap="round"/>
    <line x1="39" y1="52.6" x2="28" y2="53.7" stroke="#cbd5e1" stroke-width="1" stroke-linecap="round"/>
    <line x1="39" y1="55.2" x2="31" y2="56" stroke="#cbd5e1" stroke-width="1" stroke-linecap="round"/>
    <g class="cti-page-turn" style="transform-origin:24px 52.5px;">
      <path d="M24 47.5 C30 45.5 36.5 45 42.5 46.5 L42.5 57.2 C36.5 55.7 30 56.2 24 58.2 Z" fill="white" stroke="#cbd5e1" stroke-width="1"/>
      <line x1="39" y1="50.5" x2="28" y2="51.6" stroke="#cbd5e1" stroke-width="1" stroke-linecap="round"/>
      <line x1="39" y1="53.6" x2="28" y2="54.7" stroke="#cbd5e1" stroke-width="1" stroke-linecap="round"/>
    </g>
    <line x1="24" y1="47.5" x2="24" y2="58.2" stroke="#004AAD" stroke-width="1.6" stroke-linecap="round"/>
    <path d="M22.4 45.5 L25.6 45.5 L25.6 53.6 L24 51.6 L22.4 53.6 Z" fill="#F59E0B"/>
  </svg>

  <svg v-else-if="category === 'mail'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="17" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="33" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <rect x="5" y="46" width="38" height="13" rx="2" fill="white" stroke="#cbd5e1" stroke-width="1.2"/>
    <path d="M5.8 46.8 L24 55 L42.2 46.8" stroke="#004AAD" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M5.8 58.2 L17 50.6" stroke="#cbd5e1" stroke-width="1" stroke-linecap="round"/>
    <path d="M42.2 58.2 L31 50.6" stroke="#cbd5e1" stroke-width="1" stroke-linecap="round"/>
    <g class="cti-mail-pop" style="transform-origin:40px 45px;">
      <circle cx="40" cy="45" r="6" fill="#F59E0B" stroke="white" stroke-width="1.5"/>
      <text x="40" y="47.6" text-anchor="middle" fill="#92400E" font-size="7.5" font-weight="bold">@</text>
    </g>
  </svg>

  <svg v-else-if="category === 'calendar'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16.5" cy="27.2" r="3.6" fill="#004AAD"/>
    <circle cx="17.5" cy="26" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32.5" cy="27.2" r="3.6" fill="#004AAD"/>
    <circle cx="33.5" cy="26" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <rect x="6" y="46" width="36" height="15.5" rx="2.5" fill="white" stroke="#cbd5e1" stroke-width="1.2"/>
    <path d="M6 48.5 Q6 46 8.5 46 L39.5 46 Q42 46 42 48.5 L42 50.2 L6 50.2 Z" fill="#004AAD"/>
    <circle cx="24" cy="56.2" r="5.6" stroke="#F59E0B" stroke-width="1.5" fill="none" class="cti-day-ring"/>
    <path d="M24 51.6 L25.1 54.7 L28.4 54.8 L25.8 56.8 L26.7 59.9 L24 58.1 L21.3 59.9 L22.2 56.8 L19.6 54.8 L22.9 54.7 Z" fill="#F59E0B"/>
    <rect x="13" y="42.4" width="3" height="6.2" rx="1.5" fill="#D97706"/>
    <rect x="32" y="42.4" width="3" height="6.2" rx="1.5" fill="#D97706"/>
  </svg>

  <svg v-else-if="category === 'exchange'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16.5" cy="27.4" r="3.6" fill="#004AAD"/>
    <circle cx="17.5" cy="26.2" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32.5" cy="27.4" r="3.6" fill="#004AAD"/>
    <circle cx="33.5" cy="26.2" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M14 37 Q24 46 34 37" stroke="#92400E" stroke-width="2.5" stroke-linecap="round" fill="none" opacity="0.8"/>
    <circle cx="12" cy="53" r="6.5" fill="#FCD34D" stroke="#D97706" stroke-width="1.4"/>
    <text x="12" y="55.6" text-anchor="middle" fill="#92400E" font-size="7" font-weight="bold">€</text>
    <g class="cti-coin-flip" style="transform-origin:36px 53px;">
      <circle cx="36" cy="53" r="6.5" fill="#F59E0B" stroke="#D97706" stroke-width="1.4"/>
      <text x="36" y="55.6" text-anchor="middle" fill="#92400E" font-size="7" font-weight="bold">$</text>
    </g>
    <g class="cti-fx-top">
      <line x1="20" y1="49.8" x2="27.4" y2="49.8" stroke="#004AAD" stroke-width="1.6" stroke-linecap="round"/>
      <path d="M28.6 49.8 L25.2 48.1 L25.2 51.5 Z" fill="#004AAD"/>
    </g>
    <g class="cti-fx-bot">
      <line x1="28" y1="56.2" x2="20.6" y2="56.2" stroke="#004AAD" stroke-width="1.6" stroke-linecap="round"/>
      <path d="M19.4 56.2 L22.8 54.5 L22.8 57.9 Z" fill="#004AAD"/>
    </g>
  </svg>

  <svg v-else-if="category === 'clipboard'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16.5" cy="27.4" r="3.6" fill="#004AAD"/>
    <circle cx="17.5" cy="26.2" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32.5" cy="27.4" r="3.6" fill="#004AAD"/>
    <circle cx="33.5" cy="26.2" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <rect x="8" y="46" width="32" height="15" rx="2.5" fill="white" stroke="#cbd5e1" stroke-width="1.2"/>
    <rect x="18" y="43.6" width="12" height="5" rx="1.8" fill="#004AAD"/>
    <rect x="21" y="42.2" width="6" height="3" rx="1.5" fill="#0056cc"/>
    <rect x="12" y="50.6" width="3.4" height="3.4" rx="0.8" fill="#eef2f8" stroke="#cbd5e1" stroke-width="0.8"/>
    <line x1="18" y1="52.3" x2="35" y2="52.3" stroke="#cbd5e1" stroke-width="1.4" stroke-linecap="round"/>
    <rect x="12" y="56" width="3.4" height="3.4" rx="0.8" fill="#eef2f8" stroke="#cbd5e1" stroke-width="0.8"/>
    <line x1="18" y1="57.7" x2="32" y2="57.7" stroke="#cbd5e1" stroke-width="1.4" stroke-linecap="round"/>
    <path d="M12.6 52.4 L13.9 53.7 L15.6 51" stroke="#004AAD" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" class="cti-check-a" stroke-dasharray="6" stroke-dashoffset="6"/>
    <path d="M12.6 57.8 L13.9 59.1 L15.6 56.4" stroke="#004AAD" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" class="cti-check-b" stroke-dasharray="6" stroke-dashoffset="6"/>
  </svg>

  <svg v-else-if="category === 'overdue'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16.5" cy="27.4" r="3.6" fill="#004AAD"/>
    <circle cx="17.5" cy="26.2" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32.5" cy="27.4" r="3.6" fill="#004AAD"/>
    <circle cx="33.5" cy="26.2" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <circle cx="17" cy="53" r="7.5" fill="#FCD34D" stroke="#D97706" stroke-width="1.4"/>
    <text x="17" y="56" text-anchor="middle" fill="#92400E" font-size="8" font-weight="bold">€</text>
    <circle cx="34" cy="55" r="8.6" stroke="#b06060" stroke-width="1.2" fill="none" class="cti-day-ring"/>
    <circle cx="34" cy="55" r="7" fill="white" stroke="#b06060" stroke-width="1.8"/>
    <line x1="34" y1="49.6" x2="34" y2="51" stroke="#b06060" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="34" y1="59" x2="34" y2="60.4" stroke="#b06060" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="28.6" y1="55" x2="30" y2="55" stroke="#b06060" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="38" y1="55" x2="39.4" y2="55" stroke="#b06060" stroke-width="1.2" stroke-linecap="round"/>
    <line x1="34" y1="55" x2="30.8" y2="52.6" stroke="#b06060" stroke-width="1.6" stroke-linecap="round"/>
    <g class="cti-clock-sec" style="transform-origin:34px 55px;">
      <line x1="34" y1="56.4" x2="34" y2="50.4" stroke="#b06060" stroke-width="1.1" stroke-linecap="round"/>
    </g>
    <circle cx="34" cy="55" r="1.4" fill="#b06060"/>
  </svg>

  <svg v-else-if="category === 'building'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="17" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="33" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <rect x="8.5" y="44.8" width="31" height="2.6" rx="1" fill="#004AAD"/>
    <rect x="10" y="47.4" width="28" height="13.6" rx="1" fill="white" stroke="#cbd5e1" stroke-width="1.2"/>
    <rect x="13.5" y="49.6" width="5" height="3.6" rx="0.6" fill="#004AAD" fill-opacity="0.35"/>
    <rect x="21.5" y="49.6" width="5" height="3.6" rx="0.6" fill="#004AAD" fill-opacity="0.35"/>
    <rect x="29.5" y="49.6" width="5" height="3.6" rx="0.6" fill="#004AAD" fill-opacity="0.35"/>
    <rect x="13.5" y="54" width="5" height="3.6" rx="0.6" fill="#004AAD" fill-opacity="0.35"/>
    <rect x="21.5" y="54" width="5" height="3.6" rx="0.6" fill="#004AAD" fill-opacity="0.35"/>
    <rect x="29.5" y="54" width="5" height="3.6" rx="0.6" fill="#004AAD" fill-opacity="0.35"/>
    <rect x="13.5" y="49.6" width="5" height="3.6" rx="0.6" fill="#FCD34D" class="cti-win-a"/>
    <rect x="21.5" y="49.6" width="5" height="3.6" rx="0.6" fill="#FCD34D" class="cti-win-b"/>
    <rect x="29.5" y="49.6" width="5" height="3.6" rx="0.6" fill="#FCD34D" class="cti-win-c"/>
    <rect x="21.5" y="57.8" width="5" height="3.2" rx="0.6" fill="#004AAD"/>
  </svg>

  <svg v-else-if="category === 'route'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="17.2" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="18.4" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="33.2" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="34.4" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <path d="M7 58 L17 51 L31 57 L41 50" stroke="#93c5fd" stroke-width="2.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="7" cy="58" r="2.6" fill="white" stroke="#93c5fd" stroke-width="1.6"/>
    <circle cx="17" cy="51" r="2.6" fill="white" stroke="#93c5fd" stroke-width="1.6"/>
    <circle cx="31" cy="57" r="2.6" fill="white" stroke="#93c5fd" stroke-width="1.6"/>
    <path d="M41 50 C41 50 37.4 45.8 37.4 43.8 C37.4 42 38.8 40.8 41 40.8 C43.2 40.8 44.6 42 44.6 43.8 C44.6 45.8 41 50 41 50 Z" fill="#F59E0B"/>
    <circle cx="41" cy="43.8" r="1.5" fill="white"/>
    <g>
      <animateMotion dur="2.6s" repeatCount="indefinite" path="M7 58 L17 51 L31 57 L41 50"/>
      <circle r="3.2" fill="#004AAD"/>
      <circle cy="-0.9" r="1.2" fill="white" fill-opacity="0.85"/>
    </g>
  </svg>

  <svg v-else-if="category === 'flag'" viewBox="0 0 48 52" :class="props.sizeClass" fill="none" style="overflow:visible">
    <circle cx="24" cy="4.6" r="3" fill="#FCD34D"/>
    <rect x="22.5" y="6" width="3" height="5.5" rx="1.5" fill="#F59E0B"/>
    <circle cx="6" cy="28" r="3.5" fill="#D97706"/>
    <circle cx="42" cy="28" r="3.5" fill="#D97706"/>
    <rect x="8" y="11" width="32" height="33" rx="12" fill="#F59E0B"/>
    <ellipse cx="20" cy="17" rx="7" ry="4" fill="white" fill-opacity="0.2" transform="rotate(-8 20 17)"/>
    <path d="M22.8 6.9 C 20.4 4, 16.2 3.6, 14.4 5.2 C 12.6 6.8, 12.6 10.4, 14.4 12 C 16.2 13.6, 20.4 13, 22.8 10.3 Z M25.2 6.9 C 27.6 4, 31.8 3.6, 33.6 5.2 C 35.4 6.8, 35.4 10.4, 33.6 12 C 31.8 13.6, 27.6 13, 25.2 10.3 Z" fill="#004AAD" stroke="#93c5fd" stroke-width="0.5" stroke-opacity="0.6"/>
    <circle cx="24" cy="8.6" r="2.2" fill="#003a8a"/>
    <circle cx="17.2" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="16.4" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="30.8" cy="6.9" r="0.85" fill="white" fill-opacity="0.55"/>
    <circle cx="31.6" cy="10.4" r="0.75" fill="white" fill-opacity="0.5"/>
    <circle cx="16" cy="26" r="6.5" fill="white"/>
    <circle cx="16" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="17" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <circle cx="32" cy="26" r="6.5" fill="white"/>
    <circle cx="32" cy="26" r="3.6" fill="#004AAD"/>
    <circle cx="33" cy="24.8" r="1.3" fill="white" fill-opacity="0.85"/>
    <path d="M16 37 Q24 43 32 37" stroke="#92400E" stroke-width="2" stroke-linecap="round" fill="none" opacity="0.7"/>
    <ellipse cx="16" cy="60.6" rx="5" ry="1.6" fill="#1e293b" fill-opacity="0.25"/>
    <line x1="16" y1="45.4" x2="16" y2="60.6" stroke="#1e293b" stroke-width="2.2" stroke-linecap="round"/>
    <circle cx="16" cy="44.8" r="1.6" fill="#F59E0B"/>
    <path fill="#004AAD">
      <animate attributeName="d" dur="2s" repeatCount="indefinite"
        values="M17.2 46.6 C24 44.5 31 48.7 38 46.6 L38 54.1 C31 56.2 24 52 17.2 54.1 Z;
                M17.2 46.6 C24 48.7 31 44.5 38 46.6 L38 54.1 C31 52 24 56.2 17.2 54.1 Z;
                M17.2 46.6 C24 44.5 31 48.7 38 46.6 L38 54.1 C31 56.2 24 52 17.2 54.1 Z"/>
    </path>
  </svg>
</template>

<style scoped>
/* ── THINKING: pupils scan side to side ── */
@keyframes cti-eye-scan {
  0%, 100% { transform: translateX(0px); }
  25%       { transform: translateX(-1.8px); }
  75%       { transform: translateX(1.8px); }
}
.cti-eye-scan {
  animation: cti-eye-scan 2.4s ease-in-out infinite;
}

/* ── THINKING: thought dots cascade (small→big, bottom→top) ── */
@keyframes cti-thought {
  0%, 25% { opacity: 0; transform: scale(0.4) translateY(4px); }
  55%, 80% { opacity: 1; transform: scale(1) translateY(0); }
  100%     { opacity: 0; transform: scale(0.6) translateY(-2px); }
}
.cti-td-a { animation: cti-thought 2s ease-in-out infinite; animation-delay: 0s; }
.cti-td-b { animation: cti-thought 2s ease-in-out infinite; animation-delay: 0.4s; }
.cti-td-c { animation: cti-thought 2s ease-in-out infinite; animation-delay: 0.8s; }

/* ── SEARCH: radar sweep in magnifying glass ── */
@keyframes cti-radar {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
.cti-radar { animation: cti-radar 1.5s linear infinite; }

/* ── SHIP: body bobs and tilts ── */
@keyframes cti-ship-bob {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  30%       { transform: translateY(-3px) rotate(2.5deg); }
  70%       { transform: translateY(2px) rotate(-2deg); }
}
.cti-ship-bob { animation: cti-ship-bob 2s ease-in-out infinite; }

/* ── SHIP: waves scroll sideways ── */
@keyframes cti-waves {
  from { transform: translateX(0); }
  to   { transform: translateX(-16px); }
}
.cti-waves { animation: cti-waves 1.2s linear infinite; }

/* ── WAREHOUSE: top box bounces ── */
@keyframes cti-box-bounce {
  0%, 100% { transform: translateY(0); }
  40%       { transform: translateY(-7px); }
  55%       { transform: translateY(-5px); }
}
.cti-box-bounce { animation: cti-box-bounce 0.9s cubic-bezier(.36,.07,.19,.97) infinite; }

/* ── USERS: person silhouettes pop in from sides ── */
@keyframes cti-person-in-l {
  0%, 100% { transform: translateX(-4px); opacity: 0; }
  30%, 70%  { transform: translateX(0px);  opacity: 1; }
}
@keyframes cti-person-in-r {
  0%, 100% { transform: translateX(4px); opacity: 0; }
  30%, 70%  { transform: translateX(0px); opacity: 1; }
}
.cti-person-l { animation: cti-person-in-l 2s ease-in-out infinite; }
.cti-person-r { animation: cti-person-in-r 2s ease-in-out infinite; animation-delay: 0.3s; }

/* ── USERS: pulse rings expand and fade ── */
@keyframes cti-pulse-ring {
  0%   { transform: scale(0.8); opacity: 0.8; }
  100% { transform: scale(1.8); opacity: 0; }
}
.cti-pulse-l { animation: cti-pulse-ring 1.5s ease-out infinite; }
.cti-pulse-r { animation: cti-pulse-ring 1.5s ease-out infinite; animation-delay: 0.4s; }

/* ── GEARS: ear bumps counter-rotate ── */
@keyframes cti-gear-cw {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes cti-gear-ccw {
  from { transform: rotate(0deg); }
  to   { transform: rotate(-360deg); }
}
.cti-gear-cw  { animation: cti-gear-cw  2s linear infinite; }
.cti-gear-ccw { animation: cti-gear-ccw 2s linear infinite; }

/* ── BELL: swings ── */
@keyframes cti-bell-swing {
  0%, 100% { transform: rotate(0deg); }
  15%       { transform: rotate(20deg); }
  45%       { transform: rotate(-20deg); }
  60%       { transform: rotate(10deg); }
  75%       { transform: rotate(-8deg); }
  88%       { transform: rotate(4deg); }
}
.cti-bell-ring { animation: cti-bell-swing 1.2s ease-in-out infinite; }

/* ── BELL: sound arc pulses ── */
@keyframes cti-ring-arc {
  0%, 100% { opacity: 0; transform: scale(0.8); }
  40%, 60%  { opacity: 1; transform: scale(1); }
}
.cti-ring-l { animation: cti-ring-arc 1.2s ease-in-out infinite; animation-delay: 0.15s; }
.cti-ring-r { animation: cti-ring-arc 1.2s ease-in-out infinite; animation-delay: 0.15s; }

/* ── CLOCK: minute hand (fast), second hand (very fast) ── */
@keyframes cti-min-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes cti-sec-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
.cti-clock-min { animation: cti-min-spin 3s linear infinite; }
.cti-clock-sec { animation: cti-sec-spin 0.8s linear infinite; }

/* ── CONTRACT: signature draws in via stroke-dashoffset, then fades ── */
@keyframes cti-sign {
  0%        { stroke-dashoffset: 58; opacity: 1; }
  60%       { stroke-dashoffset: 0;  opacity: 1; }
  80%, 100% { stroke-dashoffset: 0;  opacity: 0; }
}
.cti-signature { animation: cti-sign 2.5s ease-in-out infinite; }

/* ── BOOK: right page flips over the spine, then a fresh page fades back in ── */
@keyframes cti-page-turn {
  0%        { transform: scaleX(1);  opacity: 1; }
  45%       { transform: scaleX(-1); opacity: 1; }
  52%       { transform: scaleX(-1); opacity: 0; }
  53%       { transform: scaleX(1);  opacity: 0; }
  62%, 100% { transform: scaleX(1);  opacity: 1; }
}
.cti-page-turn { animation: cti-page-turn 2.4s ease-in-out infinite; }

/* ── MAIL: the @ badge pops ── */
@keyframes cti-mail-pop {
  0%, 65%, 100% { transform: scale(1); }
  75%           { transform: scale(1.2); }
  85%           { transform: scale(0.96); }
}
.cti-mail-pop { animation: cti-mail-pop 2s ease-in-out infinite; }

/* ── CALENDAR: the marked day sends out a ring ── */
@keyframes cti-day-ring {
  0%   { transform: scale(0.45); opacity: 0.9; }
  100% { transform: scale(1.05); opacity: 0; }
}
.cti-day-ring { animation: cti-day-ring 1.6s ease-out infinite; }

/* ── EXCHANGE: arrows nudge apart while the second coin flips ── */
@keyframes cti-fx-top {
  0%, 100% { transform: translateX(0); }
  50%      { transform: translateX(2px); }
}
@keyframes cti-fx-bot {
  0%, 100% { transform: translateX(0); }
  50%      { transform: translateX(-2px); }
}
@keyframes cti-coin-flip {
  0%, 55%, 100% { transform: scaleX(1); }
  70%           { transform: scaleX(0.12); }
  85%           { transform: scaleX(1); }
}
.cti-fx-top    { animation: cti-fx-top 1.6s ease-in-out infinite; }
.cti-fx-bot    { animation: cti-fx-bot 1.6s ease-in-out infinite; }
.cti-coin-flip { animation: cti-coin-flip 1.6s ease-in-out infinite; }

/* ── CLIPBOARD: checkmarks draw in one after the other ── */
@keyframes cti-check-draw {
  0%, 10%   { stroke-dashoffset: 6; }
  35%, 80%  { stroke-dashoffset: 0; }
  95%, 100% { stroke-dashoffset: 6; }
}
.cti-check-a { animation: cti-check-draw 2.4s ease-in-out infinite; }
.cti-check-b { animation: cti-check-draw 2.4s ease-in-out infinite; animation-delay: 0.35s; }

/* ── BUILDING: office windows light up one after the other ── */
@keyframes cti-window {
  0%, 100% { opacity: 0; }
  35%, 70% { opacity: 1; }
}
.cti-win-a { animation: cti-window 2.4s ease-in-out infinite; }
.cti-win-b { animation: cti-window 2.4s ease-in-out infinite; animation-delay: 0.4s; }
.cti-win-c { animation: cti-window 2.4s ease-in-out infinite; animation-delay: 0.8s; }
</style>
